import React from 'react';

// Store
import { withStore } from '../../stores/RootStore';

// Helpers
import { focusable } from '../../helpers';

// Components
import Countdown from './Countdown';
import Select from './Select';

/**
 * Renders a pop-up dialog component
 * 
 * @param {object} props
 * @property {object} content
 * @property {Function} [onCancel] Optional callback to fire when the user clicks cancel
 * @property {Function} [onChange] Optional callback to fire when a select element changes (requires options property)
 * @property {Function} [onConfirm] Optional callback to fire when the user clicks confirm
 * @property {Function} [onTimeout] Optional callback to fire when a timeout ends (requires timeout property)
 * @property {Array} [options] Optional array of options to fill a select element with
 * @property {number} [timeout] Optional number of seconds to show as a countdown timer
 * @property {string} [type] Optionally dictates the aria role of the dialog
 * @returns {Function}
 */
const Dialog = props => {
  // Get values from props
  const {
    content,
    onCancel,
    onChange,
    onConfirm,
    onTimeout,
    options,
    timeout,
    type
  } = props;

  // Set up state
  const [focusIndex, setFocusIndex] = React.useState(0);
  const [exiting, setExiting] = React.useState(false);

  // Create refs
  const dialogRoot = React.useRef(null);

  // Create an array to hold focusable child elements.
  let _focusableElements = [];

  // Main useEffect hook
  React.useEffect(() => {
    // Add focusable child elements to
    // the array and focus the first one.
    if (!_focusableElements.length) {
      _focusableElements = dialogRoot.current.querySelectorAll(focusable);
    }

    _focusableElements[focusIndex].focus();
  }, [focusIndex])

  /**
   * Focus appropriate elements
   * 
   * @function focusElement
   * @param {number} index
   */
  const focusElement = index => {
    setFocusIndex(index);
  }

  /**
   * Handle keyboard interaction
   * 
   * @function handleKeyDown
   * @param {event} e
   */
  const handleKeyDown = e => {
    const first = 0;
    const last = _focusableElements.length - 1;

    const ESCAPE = "Escape";
    const TAB = "Tab";
    const SHIFT = e.shiftKey;

    // Tab Key behavior
    if (e.key === TAB && !SHIFT) {
      e.preventDefault();
      
      if (focusIndex < last) {
        focusElement(focusIndex + 1);
      } else {
        focusElement(first);
      }
    }

    // Shift + Tab Key behavior
    if (e.key === TAB && SHIFT) {
      e.preventDefault();
      if (focusIndex > 0) {
        focusElement(focusIndex - 1);
      } else {
        focusElement(last);
      }
    }

    // Escape Key behavior
    if (e.key === ESCAPE) {
      handleCancel(e);
    }
  }

  /**
   * Handle dialog cancellation
   * 
   * @function handleCancel
   */
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }

    handleClose();
  }

  /**
   * Handle dialog confirmation
   * 
   * @function handleConfirm
   */
  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }

    handleClose();
  }

  /**
   * Handle dialog close
   * 
   * @function handleClose
   */
  const handleClose = () => {
    const { onClose, store } = props;
    const { dialogStore } = store;

    if (onClose) {
      onClose();
    }

    setExiting(true);

    // Wait for the animation to finish
    // and then remove the toast.
    setTimeout(() => {
      dialogStore.closeDialog();
    }, 250);
  }

  /**
   * Convey the urgency of the message to assistive technology
   * 
   * @function renderRole
   * @param {string} type
   * @returns {string}
   */
  const renderRole = type => {
    let role;

    switch (type) {
      case 'confirm':
      case 'alert':
      case 'error':
        role = 'alertdialog';
        break;

      case 'wide':
      case 'html':
      case 'info':
        role = 'dialog';
        break;

      default:
        role = 'dialog';
    }

    return role;
  }

  return (
    <div
      aria-describedby="dialog-description"
      aria-labelledby="dialog-title"
      aria-modal="true"
      className={`dialog${
        type ? ` dialog--${type}` : ''
      }${
        exiting ? ' dialog--exiting' : ''
      }`}
      id="dialog-root"
      onKeyDown={handleKeyDown}
      ref={dialogRoot}
      role={renderRole(type)}
    >
      <div
        className={`dialog__content${type ? ` dialog__content--${type}` : ""
        }`}
      >
        <div className="dialog__head">
          <h2 id="dialog-title" className="dialog__title">
            {content.heading}
          </h2>

          {timeout && (
            <Countdown
              {...props}
              className="dialog__countdown"
              onTimeout={onTimeout}
              timeout={timeout}
            />
          )}
        </div>

        <p className="dialog__body" id="dialog-description">{content.body}</p>

        {options &&
          <Select
            className="dialog__select"
            onChange={onChange}
            options={options}
          />
        }

        <div className="dialog__action">
          {type === 'confirm' &&
            <button
              className="btn btn--outline"
              onClick={handleCancel}
            >
              {content.cancel}
            </button>
          }

          <button
            className="btn"
            onClick={handleConfirm}
          >
            {content.button}
          </button>
        </div>
      </div>
    </div>
  )
}

export default withStore(Dialog);